import { useEffect, useState } from 'react'
import {
    ASSOCIATED_PRODUCTS_CONFIG_FALLBACK,
    AssociatedProductsConfig,
    AssociatedProductsContext,
    getAssociatedProductsConfig
} from './getAssociatedProductsConfig'

/**
 * EVO-263: expone la config del bloque de colores asociados a los componentes.
 *
 * Arranca con los valores de fallback (para que el primer render — tambien el
 * del SSR / prerender — ya pinte algo coherente) y se actualiza cuando llega la
 * respuesta. La peticion esta memoizada a nivel de modulo, asi que una grilla
 * de PLP con N tiles sigue haciendo UNA sola llamada.
 */
export const useAssociatedProductsConfig = (): AssociatedProductsConfig => {
    const [config, setConfig] = useState<AssociatedProductsConfig>(
        ASSOCIATED_PRODUCTS_CONFIG_FALLBACK
    )

    useEffect(() => {
        let cancelled = false

        getAssociatedProductsConfig()
            .then((resolved) => {
                if (!cancelled) setConfig(resolved)
            })
            .catch(() => {
                if (!cancelled) setConfig(ASSOCIATED_PRODUCTS_CONFIG_FALLBACK)
            })

        return () => {
            cancelled = true
        }
    }, [])

    return config
}

/** Tope de miniaturas visibles para el contexto pedido. */
export const useAssociatedProductsLimit = (
    context: AssociatedProductsContext
): number => {
    const { limits } = useAssociatedProductsConfig()

    return (
        limits[context] ?? ASSOCIATED_PRODUCTS_CONFIG_FALLBACK.limits[context]
    )
}

/**
 * Cascada del titulo del bloque: atributo del producto -> default de la config
 * -> texto traducido del storefront. Pura, para poder testearla sin render.
 */
export const resolveAssociatedProductsTitle = (
    productTitle?: string | null,
    configDefaultTitle?: string | null,
    translatedFallback?: string | null
): string => {
    const candidates = [productTitle, configDefaultTitle, translatedFallback]

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim()
        }
    }

    return ''
}

/** Version hook de la cascada: toma el default de la config por si solo. */
export const useAssociatedProductsTitle = (
    productTitle?: string | null,
    translatedFallback?: string | null
): string => {
    const { defaultTitle } = useAssociatedProductsConfig()

    return resolveAssociatedProductsTitle(
        productTitle,
        defaultTitle,
        translatedFallback
    )
}
